import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import { openModalAction } from '../../../actions/modal';
import { PageHeader } from '../../../components/PageHeader';
import { Button } from '../../../components/PageHeader/Button';
import { TextInput } from '../../../components/PageHeader/TextInput';
import { HEADER_BUTTON_BLUE } from '../../../constants';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import Strings from '../../../Strings';
import { actions } from '../redux/actions';
import { lastActivityDisplay, toolTipForTable } from '../../../utils';
import {
  getSearchParamFromUrl,
  hydrateTableRequestFromUrl,
  saveSearchParamToUrl,
  useGlobalPageSize,
} from '../../../utils/tableUtils';

export const orgGroupsUrl = '/org-groups';

function OrganizationGroups(props) {
  const { getOrganizationGroups, editOrganizationGroup, onNavigate, openConfirmModal, getUsers } = props;
  const globalPageSize = useGlobalPageSize();
  const pageRequest = React.useRef({ offset: 0, limit: globalPageSize, search: getSearchParamFromUrl() });
  const [groups, setGroups] = React.useState([]);
  const [pagination, setPagination] = React.useState({ offset: 0, totalRecords: 0 });
  const [isLoading, setIsLoading] = React.useState(false);
  const [searchText, setSearchText] = React.useState(getSearchParamFromUrl() || '');

  const loadGroups = () => {
    setIsLoading(true);
    getOrganizationGroups(pageRequest.current).then(resp => {
      setGroups(resp?.response?.data || []);
      setPagination(resp?.response?.pagination || { offset: 0, totalRecords: 0 });
      setIsLoading(false);
    });
  };

  React.useEffect(() => {
    hydrateTableRequestFromUrl(pageRequest.current, globalPageSize);
    loadGroups();
  }, []);

  const onOffsetChange = (offset, limit) => {
    pageRequest.current.offset = offset;
    if (limit) {
      pageRequest.current.limit = limit;
    }
    loadGroups();
  };

  const onSortingChange = sorting => {
    if (sorting && sorting.length > 0) {
      pageRequest.current.offset = 0;
      pageRequest.current.sortColumn = sorting[0].id;
      pageRequest.current.sortType = sorting[0].desc ? 'descending' : 'ascending';
      loadGroups();
    }
  };

  const onPageSizeChange = newSize => {
    pageRequest.current.offset = 0;
    pageRequest.current.limit = newSize;
    loadGroups();
  };

  const onSearchQueryChange = query => {
    setSearchText(query);
    pageRequest.current.offset = 0;
    pageRequest.current.search = query;
    saveSearchParamToUrl(query);
    setTimeout(() => {
      loadGroups();
    }, 1000);
  };

  const getHeaderComponents = () => {
    const { addOrganizationGroup } = props;
    return (
      <>
        <TextInput class="search" placeholder={Strings.search} value={searchText} onChange={e => onSearchQueryChange(e.target.value)} />
        <Button class={HEADER_BUTTON_BLUE} onClick={() => addOrganizationGroup(loadGroups)} title={Strings.addOrganizationGroup} />
      </>
    );
  };

  const onRowClick = row => {
    if (row?.id) {
      onNavigate(`${orgGroupsUrl}/${row.id}/organizations`);
    }
  };

  const onOrgGrpEdit = (id) => {
    const group = groups[id];
    getUsers(group.id).then(resp => {
      editOrganizationGroup({ group, users: resp?.response, onSuccess: loadGroups });
    });
  };

  const onOrgGrpDelete = (id) => {
    const group = groups[id];
    const data = {
      title: (
        <span>
          {Strings.deleteOrgGrpWarning}
          <b> {group.name}</b>
          ?
        </span>
      ),

      onConfirmAction: actions.deleteOrganizationGroup(group.id),
      onCancelAction: null,
      confirmPostAction: loadGroups,
    };
    openConfirmModal(data);
  };

  const buttons = [
    {
      icon: 'edit',
      onClick: onOrgGrpEdit,
      disabled: (id) => {
        const group = groups[id];
        return group.role !== 'owner' && group.role !== 'maintainer';
      },
      text: Strings.edit,
      disabledTooltip: Strings.youDontHaveRightsToEditGroup,
    },
    {
      icon: 'delete',
      onClick: onOrgGrpDelete,
      disabled: (id) => {
        const group = groups[id];
        return group.role !== 'owner' && group.role !== 'maintainer';
      },
      text: Strings.delete,
      disabledTooltip: Strings.youDontHaveRightsToDeleteGroup,
    },
  ];

  const columns = [
    columnHelper.accessor('name', {
      header: Strings.name,
    }),
    columnHelper.accessor('created_at', {
      header: Strings.createdAt,
      cell: info => lastActivityDisplay(info.getValue()),
    }),
    columnHelper.accessor('created_by', {
      header: Strings.createdBy,
    }),
    columnHelper.accessor('role', {
      header: Strings.role,
    }),
    columnHelper.accessor('organizations', {
      header: Strings.organizations,
      cell: info => {
        const orgs = info.getValue();
        return orgs ? toolTipForTable(orgs.length, orgs.map(o => o.name)?.join(", ")) : null;
      },
    }),
    columnHelper.accessor('maintainers', {
      header: Strings.maintainers,
      cell: info => {
        const maintainers = info.getValue();
        return maintainers ? toolTipForTable(maintainers.length, maintainers.map(o => o.name)?.join(", ")) : null;
      },
    }),
  ];

  return (
    <>
      <PageHeader right={getHeaderComponents()} left={Strings.organizationGroups} />
      <Table3
        name="organization-groups"
        data={groups || []}
        columns={columns}
        isLoading={isLoading}
        onRowClick={onRowClick}
        onOffsetChange={onOffsetChange}
        onSortingChange={onSortingChange}
        onPageSizeChange={onPageSizeChange}
        pagination={pagination}
        buttons={buttons}
      />
    </>
  );
}

const mapDispatchToProps = dispatch => ({
  onNavigate: path => dispatch(push(path)),
  getOrganizationGroups: pageRequest => dispatch(actions.getOrganizationGroups(pageRequest)),
  addOrganizationGroup: onSuccess => dispatch(openModalAction('edit-organization-group', { onSuccess })),
  editOrganizationGroup: data => dispatch(openModalAction('edit-organization-group', data)),
  getUsers: id => dispatch(actions.getGroupUsers(id)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
});

OrganizationGroups.propTypes = {
  getOrganizationGroups: PropTypes.func.isRequired,
  addOrganizationGroup: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  editOrganizationGroup: PropTypes.func.isRequired,
  getUsers: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
};

export default connect(null, mapDispatchToProps)(OrganizationGroups);
